import { useQuery } from '@apollo/client';
import type { Provider } from '@openx/types/provider.ts';
import { useCallback, useMemo } from 'react';
import { useAuth } from '../../../components/AuthContext/context.ts';
import { GetProvidersDocument } from '../../../graphql/query/audiences/GetProviders.generated.tsx';
import { isAdmin } from '../../../permissions/validators.ts';
import { type ExportTypeValue, exportType } from '../AudienceForm/constants.ts';

export const DEFAULT_PROVIDER_NAME = 'OpenXSelect';
export const DEFAULT_PROVIDER_ID = 'openaudience';

export type GetProviderParam = string | undefined | null;

export type ProviderWithCapabilities = Provider & {
  supportsBidstream: boolean;
  supportsBidstreamPlus: boolean;
};

export type UseProvidersResponse = {
  providers: Pick<Provider, 'id' | 'name' | 'allowed_export_types'>[];
  allowedProviders: ProviderWithCapabilities[];
  getProviderName: (provider_name: GetProviderParam) => string;
  getAllowedExportTypes: (provider_name: GetProviderParam) => string[];
  loadingProviders: boolean;
  hasProvidersWithBidstream: boolean;
  hasProvidersWithBidstreamPlus: boolean;
  getProvidersByExportType: (exportType: ExportTypeValue | null) => ProviderWithCapabilities[];
};

export const useProviders = (accountId?: string): UseProvidersResponse => {
  const { session } = useAuth();
  const accountIdVariables = isAdmin(session) ? { accountId } : {};
  const { data: providersList, loading: loadingProviders } = useQuery(GetProvidersDocument, {
    variables: { ...accountIdVariables },
  });

  const {
    extractedProviders,
    allowedProviders: baseAllowedProviders,
    providersMap,
  } = useMemo(() => {
    const providers = providersList?.provider || [];
    const initialProviders = {
      extractedProviders: [] as Pick<Provider, 'id' | 'name' | 'allowed_export_types'>[],
      allowedProviders: [] as Provider[],
      providersMap: {} as Record<string, Provider>,
    };

    return providers.reduce((acc, provider) => {
      if (provider?.id) {
        acc.extractedProviders.push(provider);
        acc.providersMap[provider.id] = provider as Provider;
        acc.allowedProviders.push(provider as Provider);
      }
      return acc;
    }, initialProviders);
  }, [providersList]);

  const allowedProviders = useMemo(() => {
    return baseAllowedProviders.map(provider => ({
      ...provider,
      supportsBidstream: (provider.allowed_export_types || []).includes(exportType.BIDSTREAM),
      supportsBidstreamPlus: (provider.allowed_export_types || []).includes(exportType.BIDSTREAM_PLUS),
    }));
  }, [baseAllowedProviders]);

  const hasProvidersWithBidstream = useMemo(
    () => allowedProviders.some(provider => provider.supportsBidstream),
    [allowedProviders]
  );

  const hasProvidersWithBidstreamPlus = useMemo(
    () => allowedProviders.some(provider => provider.supportsBidstreamPlus),
    [allowedProviders]
  );

  const getProviderName = useCallback(
    (provider_name: GetProviderParam): string => (provider_name ? providersMap[provider_name]?.name || '' : ''),
    [providersMap]
  );

  const getAllowedExportTypes = useCallback(
    (provider_name: GetProviderParam): string[] =>
      provider_name ? providersMap[provider_name]?.allowed_export_types || [] : [],
    [providersMap]
  );

  const getProvidersByExportType = useCallback(
    (exportTypeInput: ExportTypeValue | null): ProviderWithCapabilities[] => {
      if (!exportTypeInput || exportTypeInput === exportType.OA_MATCH) {
        return [];
      }

      if (exportTypeInput === exportType.BIDSTREAM) {
        return allowedProviders.filter(provider => provider.supportsBidstream);
      }

      if (exportTypeInput === exportType.BIDSTREAM_PLUS) {
        return allowedProviders.filter(provider => provider.supportsBidstreamPlus);
      }

      return [];
    },
    [allowedProviders]
  );

  return {
    providers: extractedProviders,
    allowedProviders,
    getProviderName,
    getAllowedExportTypes,
    loadingProviders,
    hasProvidersWithBidstream,
    hasProvidersWithBidstreamPlus,
    getProvidersByExportType,
  };
};
